import React, { useEffect, useRef } from "react";
import {
  Sparkles,
  Code,
  Shield,
  Settings,
  Server,
  Layout,
  Database,
  Wrench,
  ShoppingCart,
  Smartphone,
  Briefcase,
  Monitor,
  Code2,
} from "lucide-react";
import "./AboutUs.css";

const AboutUs = () => {
  const statsRef = useRef(null);
  const countersAnimated = useRef(false);

  // Animate counters when stats section scrolls into view
  useEffect(() => {
    const animateCounters = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !countersAnimated.current) {
          countersAnimated.current = true;

          const statNumbers = document.querySelectorAll("[data-count]");
          const duration = 2000;
          const startTime = Date.now();

          const updateCounters = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            statNumbers.forEach((statNumber) => {
              const target = +statNumber.getAttribute("data-count");
              const currentValue = Math.floor(progress * target);
              statNumber.textContent = currentValue + "+";
            });

            if (progress < 1) {
              requestAnimationFrame(updateCounters);
            }
          };

          requestAnimationFrame(updateCounters);
        }
      });
    };

    const observer = new IntersectionObserver(animateCounters, {
      threshold: 0.5,
      rootMargin: "0px 0px -50px 0px",
    });

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  // Scroll-triggered animations for other elements
  useEffect(() => {
    const animatedElements = document.querySelectorAll(".animate-on-scroll");

    const observerOptions = {
      threshold: 0.1,
    };

    const animationObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;

          // Add animation classes based on element type
          if (
            el.classList.contains("section-header") ||
            el.classList.contains("mission-statement") ||
            el.classList.contains("tech-stack-section")
          ) {
            el.classList.add("animate-fade-up");
          } else if (
            el.classList.contains("highlight-card") ||
            el.classList.contains("stat-item") ||
            el.classList.contains("tech-category")
          ) {
            el.classList.add("animate-fade-in");
          } else {
            el.classList.add("animate-fade-up");
          }

          observer.unobserve(el);
        }
      });
    }, observerOptions);

    animatedElements.forEach((el) => {
      animationObserver.observe(el);
    });

    return () => {
      animatedElements.forEach((el) => animationObserver.unobserve(el));
    };
  }, []);

  const highlights = [
    {
      icon: <Sparkles className="highlight-icon" />,
      title: "Innovation First",
      description:
        "We stay ahead of technology trends to deliver solutions that give your business a competitive advantage",
    },
    {
      icon: <Code className="highlight-icon" />,
      title: "Technical Excellence",
      description: "Our team combines deep expertise with hands-on experience across modern technology stacks",
    },
    {
      icon: <Shield className="highlight-icon" />,
      title: "Security & Reliability",
      description: "We build enterprise-grade systems you can trust, with security and stability at the core",
    },
  ];

  const techStack = {
    "ERP Systems": ["Microsoft Dynamics NAV", "Business Central", "Power BI"],
    "Backend Development": [".NET", "C#", "REST APIs", "SQL Server"],
    "Frontend Development": ["HTML", "CSS", "JavaScript", "React", "Angular"],
    Databases: ["Microsoft SQL Server", "MySQL"],
    "DevOps & Integration": [
      "Git",
      "GitHub",
      "CI/CD",
      "Azure DevOps",
      "PowerShell",
    ],
    "E-Commerce & Retail": [
      "Custom POS Systems",
      "WMS Integration",
      "eCommerce Gateways",
    ],
    "Mobile & Web Platforms": [
      "Android",
      "iOS",
      "Progressive Web App",
      "Responsive Web Apps",
    ],
    "Business Services": [
      "Business Analysis",
      "Customization",
      "System Integration",
      "Accounting Support",
      "Digital Transformation",
    ],
    "IT Services": [
      "System Administration",
      "IT Infrastructure",
      "Networking",
      "Helpdesk Support",
    ],
  };

  const getTechIcon = (category) => {
    switch (category) {
      case "ERP Systems":
        return <Settings className="tech-category-icon" />;
      case "Backend Development":
        return <Server className="tech-category-icon" />;
      case "Frontend Development":
        return <Layout className="tech-category-icon" />;
      case "Databases":
        return <Database className="tech-category-icon" />;
      case "DevOps & Integration":
        return <Wrench className="tech-category-icon" />;
      case "E-Commerce & Retail":
        return <ShoppingCart className="tech-category-icon" />;
      case "Mobile & Web Platforms":
        return <Smartphone className="tech-category-icon" />;
      case "Business Services":
        return <Briefcase className="tech-category-icon" />;
      case "IT Services":
        return <Monitor className="tech-category-icon" />;
      default:
        return <Code2 className="tech-category-icon" />;
    }
  };

  return (
    <section id="about" className="about-section">
      <div className="about-container">
        {/* Add animate-on-scroll to section header */}
        <div className="section-header animate-on-scroll">
          <h2>
            <span className="header-gradient">About</span> Katrori ITS
          </h2>
          <div className="underline-animation"></div>
        </div>

        {/* Mission statement with animate-on-scroll */}
        <div className="mission-statement animate-on-scroll">
          <p className="lead-text">
            For over a decade, we've been helping businesses across Kosovo and the Balkans{" "}
            <span className="text-highlight">
              transform through technology
            </span>
            . From enterprise ERP systems to custom mobile solutions, we build software that solves real business challenges and drives measurable growth.
          </p>
        </div>

        {/* Highlights - add animate-on-scroll to each card */}
        <div className="highlights-grid">
          {highlights.map((item, index) => (
            <div key={index} className="highlight-card animate-on-scroll">
              <div className="icon-wrapper">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>

        {/* Stats section */}
        <div className="stats-section" ref={statsRef}>
          <div className="stat-item animate-on-scroll">
            <div className="stat-number" data-count="10">
              0+
            </div>
            <div className="stat-label">Years Experience</div>
          </div>
          <div className="stat-item animate-on-scroll">
            <div className="stat-number" data-count="150">
              0+
            </div>
            <div className="stat-label">Projects Delivered</div>
          </div>
          <div className="stat-item animate-on-scroll">
            <div className="stat-number" data-count="85">
              0+
            </div>
            <div className="stat-label">Clients</div>
          </div>
          {/* <div className="stat-item animate-on-scroll">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support</div>
          </div> */}
        </div>

        {/* Tech stack section */}
        <div className="tech-stack-section animate-on-scroll">
          <h3>Technology We Work With</h3>
          <p className="tech-stack-subtitle">
            We use proven, industry-standard technologies to build reliable solutions that scale with your business
          </p>

          <div className="tech-stack-grid">
            {Object.entries(techStack).map(([category, technologies]) => (
              <div key={category} className="tech-category animate-on-scroll">
                <div className="tech-category-header">
                  {getTechIcon(category)}
                  <h4>{category}</h4>
                </div>
                <div className="tech-items">
                  {technologies.map((tech, index) => (
                    <span key={index} className="tech-item">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
